import React, { useRef, useState } from 'react';
import { Button } from '../../../components/common';
import { expenseService, getPhotoUrl } from '../../../services/resources';
import { apiError } from '../../../services/api';

const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png'];
const MAX_BYTES = 1024 * 1024;

/**
 * Photo widget for the Expense edit form. Same pattern as the Customer photo
 * widget — the photo lives in a separate cTrxBiayaImage table, so there's no
 * cheap "has photo" flag on the expense row; always attempt the image and
 * fall back to "No photo" on a 404 via onError.
 */
export default function PhotoUpload({ expense, onUploaded, onToast }) {
  const inputRef = useRef();
  const [imgFailed, setImgFailed] = useState(false);
  const [version, setVersion] = useState(0); // cache-bust after upload/remove
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const baseUrl = expense?.id ? getPhotoUrl('expense', expense.id) : null;
  const photoUrl = baseUrl ? `${baseUrl}?v=${version}` : null;
  const hasPhoto = photoUrl && !imgFailed;

  const handleRemove = async () => {
    setError('');
    setBusy(true);
    try {
      await expenseService.removePhoto(expense.id);
      setImgFailed(true);
      onToast?.('Photo removed');
      onUploaded?.();
    } catch (err) {
      setError(apiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (file) => {
    setError('');
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      setError('Only JPG, JPEG or PNG allowed');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Photo must be 1MB or smaller');
      return;
    }
    setBusy(true);
    try {
      await expenseService.uploadPhoto(expense.id, file);
      setImgFailed(false);
      setVersion((v) => v + 1);
      onToast?.('Photo uploaded');
      onUploaded?.();
    } catch (err) {
      setError(apiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-gray-700">Receipt Photo</span>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFile(e.dataTransfer.files[0]);
        }}
        className={`flex items-center gap-4 rounded-md border-2 border-dashed p-3 ${
          dragOver ? 'border-brand bg-brand-light' : 'border-gray-300'
        }`}
      >
        {hasPhoto ? (
          <img
            key={photoUrl}
            src={photoUrl}
            alt="receipt"
            className="h-16 w-16 rounded object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded bg-gray-100 text-xs text-gray-400">
            No photo
          </div>
        )}
        <div className="text-sm text-gray-500">
          <p>Drag &amp; drop or</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {busy ? 'Working…' : 'Choose file'}
            </Button>
            {hasPhoto && (
              <Button size="sm" variant="danger" disabled={busy} onClick={handleRemove}>
                Remove
              </Button>
            )}
          </div>
          <p className="mt-1 text-xs">JPG/PNG, max 1MB</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
        />
      </div>
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </div>
  );
}
